/**
 * LeadTimelineDrawer
 *
 * Privacy-first lead detail panel for partners and admins.
 *
 *  - Shows the Protected Lead Profile (PII-stripped: first name only,
 *    no email/phone — replaced by a privacy badge).
 *  - Renders the full lifecycle timeline (events from `lead_events`).
 *  - Lets the actor advance the lead through stages (forward-only).
 *  - Lets the actor log a free-form note / call / quotation event.
 *
 * Props:
 *  - leadId: string                       (required)
 *  - onClose: () => void                  (required)
 *  - canEdit: boolean (default true)       false ⇒ read-only mode
 */
import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Shield, Clock, Lock, ChevronRight, MapPin, IndianRupee,
  Phone, Mail, FileText, ArrowRight, CheckCircle2, Loader2,
  MessageSquare, Send, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_COLORS = {
  created: 'bg-white/10 text-white/80 ring-white/15',
  assigned: 'bg-blue-500/15 text-blue-300 ring-blue-500/30',
  contact_attempted: 'bg-purple-500/15 text-purple-300 ring-purple-500/30',
  appointment_booked: 'bg-cyan-500/15 text-cyan-300 ring-cyan-500/30',
  meeting_completed: 'bg-indigo-500/15 text-indigo-300 ring-indigo-500/30',
  quotation_shared: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  follow_up: 'bg-orange-500/15 text-orange-300 ring-orange-500/30',
  closed_won: 'bg-emerald-500/20 text-emerald-300 ring-emerald-500/40',
  closed_lost: 'bg-red-500/15 text-red-300 ring-red-500/30',
};

const EVENT_ICONS = {
  stage_changed: ChevronRight,
  note_added: FileText,
  call_logged: Phone,
  email_sent_via_dreamlife: Mail,
  sms_sent_via_dreamlife: MessageSquare,
  quotation_uploaded: FileText,
  site_visit_scheduled: MapPin,
  contact_reveal_requested: Lock,
  contact_revealed: Lock,
  customer_message: MessageSquare,
  customer_viewed_quotation: CheckCircle2,
  customer_accepted_quotation: CheckCircle2,
  customer_requested_callback: Phone,
  customer_cancelled: X,
  lead_assigned: Sparkles,
  lead_unassigned: X,
  lead_reopened: Sparkles,
};

const formatStage = (stage = 'created', labels = {}) =>
  labels[stage] || stage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const PrivacyBadge = () => (
  <span
    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-gold/10 ring-1 ring-gold/30 text-gold text-[10px] uppercase tracking-[0.18em] font-semibold"
    data-testid="lead-privacy-badge"
  >
    <Shield className="w-3 h-3" /> Privacy Protected
  </span>
);

const StageBadge = ({ stage, labels }) => (
  <span
    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ring-1 text-xs font-semibold tracking-[0.12em] uppercase ${STAGE_COLORS[stage] || STAGE_COLORS.created}`}
    data-testid={`lead-stage-badge-${stage}`}
  >
    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
    {formatStage(stage, labels)}
  </span>
);

const ProfileField = ({ label, value, locked = false, mono = false }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-text-secondary mb-1 flex items-center gap-1.5">
      {label}
      {locked && <Lock className="w-3 h-3 opacity-60" />}
    </div>
    <div className={`text-sm ${mono ? 'font-mono' : ''} ${locked ? 'text-text-secondary italic' : 'text-white'}`}>
      {locked ? 'Hidden by ASTIIV BASGAT' : (value || '—')}
    </div>
  </div>
);

const LeadTimelineDrawer = ({ leadId, onClose, canEdit = true }) => {
  const [lead, setLead] = useState(null);
  const [events, setEvents] = useState([]);
  const [stages, setStages] = useState({ stages: [], labels: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newStage, setNewStage] = useState('');
  const [stageNote, setStageNote] = useState('');
  const [eventType, setEventType] = useState('note_added');
  const [eventDesc, setEventDesc] = useState('');
  const [eventVisible, setEventVisible] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [protectedRes, timelineRes, metaRes] = await Promise.all([
        axios.get(`${API}/leads/${leadId}/protected`),
        axios.get(`${API}/leads/${leadId}/timeline`),
        stages.stages.length ? Promise.resolve({ data: stages }) : axios.get(`${API}/leads/meta/stages`),
      ]);
      setLead(protectedRes.data);
      setEvents(timelineRes.data.events || []);
      setStages(metaRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load lead');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  useEffect(() => { refresh(); }, [refresh]);

  const advanceStage = async () => {
    if (!newStage) { toast.error('Pick a stage'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/leads/${leadId}/stage`, {
        to_stage: newStage,
        note: stageNote.trim(),
      });
      toast.success(`Stage: ${formatStage(data.from_stage, stages.labels)} → ${formatStage(data.to_stage, stages.labels)}`);
      setNewStage('');
      setStageNote('');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to advance stage');
    } finally {
      setSaving(false);
    }
  };

  const logEvent = async () => {
    if (!eventDesc.trim()) { toast.error('Add a short description'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/leads/${leadId}/events`, {
        event_type: eventType,
        description: eventDesc.trim(),
        customer_visible: eventVisible,
      });
      toast.success('Event logged');
      setEventDesc('');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to log event');
    } finally {
      setSaving(false);
    }
  };

  // Stages the partner may advance to from current stage (forward-only)
  const allStages = stages.stages || [];
  const currentIdx = allStages.indexOf(lead?.stage || 'created');
  const forwardStages = currentIdx >= 0
    ? allStages.slice(currentIdx + 1)
    : allStages;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-stretch justify-end bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="lead-timeline-drawer"
    >
      <motion.div
        initial={{ x: 60 }} animate={{ x: 0 }} exit={{ x: 60 }}
        transition={{ type: 'spring', stiffness: 220, damping: 30 }}
        className="w-full sm:max-w-xl bg-[#0c0c0c] border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0c0c0c]/95 backdrop-blur-lg border-b border-white/5 p-5 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <PrivacyBadge />
              {lead?.stage && <StageBadge stage={lead.stage} labels={stages.labels} />}
            </div>
            <h2 className="text-xl font-semibold text-white" data-testid="lead-drawer-customer-name">
              {lead?.customer_first_name || 'Member'}
            </h2>
            <p className="text-xs text-text-secondary font-mono mt-0.5">
              {lead?.dl_lead_id || '—'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-lg" data-testid="lead-drawer-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && !lead ? (
          <div className="flex items-center justify-center py-20 text-text-secondary">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="p-5 space-y-6">
            {/* Privacy notice */}
            <div className="rounded-lg border border-gold/20 bg-gold/5 p-3 flex items-start gap-2.5">
              <Shield className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
              <p className="text-xs text-white/75 leading-relaxed">
                ASTIIV BASGAT protects member privacy. You see anonymised details only.
                Contact happens through ASTIIV BASGAT — log a <em>Contact Attempt</em>
                event below to request a callback or message.
              </p>
            </div>

            {/* Protected profile fields */}
            <div className="grid grid-cols-2 gap-4">
              <ProfileField label="First Name" value={lead?.customer_first_name} />
              <ProfileField label="Email" locked />
              <ProfileField label="Phone" locked />
              <ProfileField label="Last Name" locked />
              <ProfileField label="Interest" value={lead?.interest} />
              <ProfileField label="Preferred Location" value={lead?.preferred_location} />
              <ProfileField label="Budget" value={lead?.budget_range || (lead?.budget_declared ? `${lead.budget_declared.toLocaleString()}` : null)} />
              <ProfileField label="Priority" value={lead?.priority || lead?.lead_tier} />
              <ProfileField label="Membership" value={lead?.membership_tier} />
              <ProfileField label="Source" value={lead?.source} />
              <ProfileField label="Region" value={lead?.region} />
              <ProfileField label="Created" value={formatTime(lead?.created_at)} />
            </div>

            {/* Stage advance */}
            {canEdit && forwardStages.length > 0 && !['closed_won', 'closed_lost'].includes(lead?.stage) && (
              <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-gold" /> Advance Stage
                </h3>
                <div className="space-y-3">
                  <Select value={newStage} onValueChange={setNewStage}>
                    <SelectTrigger className="bg-white/5 border-white/10" data-testid="stage-advance-select">
                      <SelectValue placeholder="Move to…" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      {forwardStages.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`stage-option-${s}`}>
                          {formatStage(s, stages.labels)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea
                    value={stageNote}
                    onChange={(e) => setStageNote(e.target.value)}
                    placeholder="Optional context (visible to the customer)…"
                    className="bg-white/5 border-white/10 min-h-[60px] text-sm"
                    data-testid="stage-note-input"
                  />
                  <Button
                    onClick={advanceStage}
                    disabled={saving || !newStage}
                    className="w-full bg-gold text-black hover:bg-gold/90 disabled:opacity-40"
                    data-testid="stage-advance-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ChevronRight className="w-4 h-4 mr-2" />}
                    Advance to {newStage ? formatStage(newStage, stages.labels) : '…'}
                  </Button>
                </div>
              </div>
            )}

            {/* Log event */}
            {canEdit && (
              <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gold" /> Log Activity
                </h3>
                <div className="space-y-3">
                  <Select value={eventType} onValueChange={setEventType}>
                    <SelectTrigger className="bg-white/5 border-white/10" data-testid="event-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      <SelectItem value="note_added">Internal Note</SelectItem>
                      <SelectItem value="call_logged">Call Logged</SelectItem>
                      <SelectItem value="email_sent_via_dreamlife">Email Sent</SelectItem>
                      <SelectItem value="sms_sent_via_dreamlife">SMS Sent</SelectItem>
                      <SelectItem value="quotation_uploaded">Quotation Uploaded</SelectItem>
                      <SelectItem value="site_visit_scheduled">Site Visit Scheduled</SelectItem>
                      <SelectItem value="contact_reveal_requested">Request Customer Contact</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    value={eventDesc}
                    onChange={(e) => setEventDesc(e.target.value)}
                    placeholder="What happened? (be concise)…"
                    className="bg-white/5 border-white/10 min-h-[70px] text-sm"
                    data-testid="event-desc-input"
                  />
                  <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
                    <input
                      type="checkbox"
                      checked={eventVisible}
                      onChange={(e) => setEventVisible(e.target.checked)}
                      data-testid="event-visible-toggle"
                    />
                    Visible to customer in their activity log
                  </label>
                  <Button
                    onClick={logEvent}
                    disabled={saving || !eventDesc.trim()}
                    className="w-full bg-white/10 hover:bg-white/15 text-white border border-white/10 disabled:opacity-40"
                    data-testid="event-log-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Log Event
                  </Button>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-gold" /> Timeline
              </h3>
              {events.length === 0 ? (
                <p className="text-text-secondary text-xs italic">No events yet.</p>
              ) : (
                <ol className="relative border-l border-white/10 pl-5 space-y-4" data-testid="lead-timeline-list">
                  {events.map((ev, i) => {
                    const Icon = EVENT_ICONS[ev.event_type] || FileText;
                    return (
                      <li
                        key={`${ev.created_at}-${i}`}
                        className="relative"
                        data-testid={`timeline-event-${i}`}
                      >
                        <span className="absolute -left-[27px] w-5 h-5 rounded-full bg-[#1a1a1a] ring-2 ring-gold/30 flex items-center justify-center">
                          <Icon className="w-2.5 h-2.5 text-gold" />
                        </span>
                        <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                          <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-gold font-semibold">
                              {(ev.event_type || '').replace(/_/g, ' ')}
                            </span>
                            <span className="text-[10px] text-text-secondary">
                              {formatTime(ev.created_at)}
                            </span>
                          </div>
                          {ev.description && (
                            <p className="text-sm text-white/85">{ev.description}</p>
                          )}
                          {ev.event_type === 'stage_changed' && ev.metadata?.to_stage && (
                            <div className="mt-2">
                              <StageBadge stage={ev.metadata.to_stage} labels={stages.labels} />
                            </div>
                          )}
                          <div className="mt-2 flex items-center gap-2 text-[11px] text-text-secondary">
                            <span>by <span className="text-white/70">{ev.actor_name || ev.actor_role}</span></span>
                            {!ev.customer_visible && (
                              <span className="px-1.5 py-0.5 rounded bg-white/5 text-[9px] uppercase tracking-wider text-text-secondary">
                                Internal
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default LeadTimelineDrawer;
